import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { FiChevronDown, FiGrid } from "react-icons/fi";

// Menu of a dashboard space, grouped by the "group" field of each route
const Sidebar = ({ isOpen, routes }) => {
  const { activeCompany } = useSelector((state) => state.companies);
  const { user } = useSelector((state) => state.authUser);
  const location = useLocation();

  const visibleRoutes = routes.filter(
    (route) =>
      route.isVisible &&
      !(route.requiresCompany && user?.role === "user" && !activeCompany),
  );

  const groups = visibleRoutes.reduce((acc, route) => {
    const name = route.group || "";
    const group = acc.find((g) => g.name === name);

    if (group) group.routes.push(route);
    else acc.push({ name, routes: [route] });

    return acc;
  }, []);

  // Collapsible services with modules, open when one module is displayed
  const [openSubgroups, setOpenSubgroups] = useState({});

  const isSubgroupOpen = (subgroup, children) =>
    openSubgroups[subgroup] ??
    children.some((route) =>
      location.pathname.startsWith(route.layout + route.path),
    );

  const toggleSubgroup = (subgroup, open) =>
    setOpenSubgroups((prev) => ({ ...prev, [subgroup]: !open }));

  const renderLink = (route) => {
    const path = route.layout + route.path;
    const active = location.pathname.startsWith(path);

    return (
      <Link
        key={path}
        to={path}
        className={`dash-nav-link ${active ? "is-active" : ""}`}
        aria-current={active ? "page" : undefined}
      >
        {!route.subgroup && (
          <span className="dash-nav-icon" aria-hidden="true">
            {route.icon}
          </span>
        )}
        {route.name}
      </Link>
    );
  };

  return (
    <aside className={`dash-sidebar ${isOpen ? "is-open" : ""}`}>
      <div className="dash-brand">
        <a href="/">
          <img
            src="/images/logo-Cplatform.png"
            alt="Platform98"
            width="180"
            height="43"
          />
        </a>
      </div>

      <nav className="dash-nav custom-scrollbar" aria-label="Menu principal">
        {groups.map((group) => {
          // A service with modules (Gestion 360) lists them right below it
          const direct = group.routes.filter((route) => !route.subgroup);
          const subgroups = [
            ...new Set(group.routes.map((r) => r.subgroup).filter(Boolean)),
          ];

          return (
            <div key={group.name || "main"} className="dash-nav-group">
              {direct.map(renderLink)}
              {subgroups.map((subgroup) => {
                const children = group.routes.filter(
                  (route) => route.subgroup === subgroup,
                );
                const open = isSubgroupOpen(subgroup, children);

                return (
                  <div key={subgroup} className="dash-nav-sub">
                    <button
                      type="button"
                      className={`dash-nav-link is-parent ${
                        open ? "is-open" : ""
                      }`}
                      aria-expanded={open}
                      onClick={() => toggleSubgroup(subgroup, open)}
                    >
                      <span className="dash-nav-icon" aria-hidden="true">
                        <FiGrid />
                      </span>
                      {subgroup}
                      <FiChevronDown
                        className="dash-nav-chevron"
                        aria-hidden="true"
                      />
                    </button>
                    {open && (
                      <div className="dash-nav-children">
                        {children.map(renderLink)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default Sidebar;
